<template>
  <div class="ai-chat-mask" v-if="visible">
    <div class="ai-chat-header">
      <button class="ai-session-trigger" type="button" aria-label="打开会话列表" @click="toggleSessionDrawer">
        <svg class="ai-session-trigger-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 7H20" />
          <path d="M4 12H15" />
          <path d="M4 17H18" />
        </svg>
      </button>
      <div class="ai-chat-title-wrap">
        <p class="ai-chat-title">{{ title }}</p>
        <p class="ai-chat-subtitle">{{ subtitle }}</p>
      </div>
      <button class="ai-chat-close" type="button" aria-label="关闭" @click="goBack()">
        <svg class="ai-chat-close-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6L18 18" />
          <path d="M18 6L6 18" />
        </svg>
      </button>
    </div>
    <div v-if="showSessionDrawer" class="ai-session-drawer-mask" @click="closeSessionDrawer">
    </div>
    <div :class="{ 'ai-session-drawer': true, 'is-open': showSessionDrawer }" @click.stop>
      <button class="ai-session-new-button" type="button" @click="createNewSession">
        + 新建会话
      </button>
      <div class="ai-session-list">
        <div v-for="session in sessionList" :key="session.id"
          :class="['ai-session-row', { 'is-active': session.id === sessionId }]">
          <button class="ai-session-item" type="button" @click="switchSession(session)">
            {{ getSessionTitle(session) }}
          </button>
          <button class="ai-session-delete" type="button" aria-label="删除会话" @click.stop="deleteSession(session)">
            <svg class="ai-session-delete-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 3h6l1 2h5v2H3V5h5l1-2z" fill="currentColor" opacity=".9" />
              <path d="M5 9h14l-1 12H6L5 9z" fill="currentColor" opacity=".85" />
            </svg>
          </button>
        </div>
        <p v-if="!sessionList.length" class="ai-session-empty">暂无会话</p>
      </div>
    </div>
    <div class="ai-chat-body" ref="aiChatBody">
      <div v-for="(message, messageIndex) in aiMessages" :key="messageIndex"
        :class="{ 'ai-message-item': true, 'from-user': message.role === 'user', 'from-ai': message.role === 'assistant' }">
        <div class="ai-message-role">{{ message.role === 'user' ? '我' : 'AI' }}</div>
        <div :class="['ai-message-body', { 'is-user': message.role === 'user' }]">
          <div v-if="message.role === 'assistant' && message.reasoning" class="ai-reasoning-wrap">
            <p class="ai-reasoning-label">思考过程</p>
            <div class="ai-reasoning-block" v-html="displayReasoningHtml(message, messageIndex)">
            </div>
          </div>
          <div v-if="message.role === 'assistant' && hasProcessParts(message)" class="ai-process-wrap">
            <p class="ai-reasoning-label">执行流</p>
            <pre class="ai-process-stream">{{ buildProcessStream(message) }}</pre>
          </div>
          <div v-if="message.content || message.role === 'user'" class="ai-message-content"
            v-html="displayMessageContentHtml(message, messageIndex)">
          </div>
        </div>
      </div>
      <!-- 加载中：仅当「当前查看的会话」仍在生成，且与后台会话生成状态一一对应 -->
      <div v-if="isCurrentSessionLoading && !hasCurrentSessionReplyContent" class="ai-message-item from-ai">
        <div class="ai-message-role">AI</div>
        <div class="ai-message-content loading-content">
          <div class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span class="loading-text">正在思考中...</span>
        </div>
      </div>
    </div>
    <div class="ai-chat-footer">
      <div class="ai-input-wrap">
        <textarea ref="aiTextarea" v-model="aiInput" class="ai-input" rows="1" maxlength="300"
          :placeholder="placeholder" @input="autoResizeTextarea" @keydown.enter="onEnterKey"
          @compositionstart="onCompositionStart" @compositionend="onCompositionEnd">
        </textarea>
        <button v-if="!isCurrentSessionLoading" class="ai-send-button" type="button" aria-label="发送"
          @click="sendMessage">
          <svg class="ai-send-icon" viewBox="0 0 24 24" fill="none">
            <path
              d="M3 11.7L20.3 3.9c.6-.3 1.3.3 1 .9l-7.8 17.3c-.3.7-1.2.7-1.5.1l-3.2-6.2-6.2-3.2c-.6-.3-.6-1.2.1-1.5z"
              fill="currentColor" />
          </svg>
        </button>
        <button v-else class="ai-stop-button" type="button" aria-label="停止生成" @click="abortRunningSession">
          <span class="ai-stop-icon" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { createOpencodeClient } from './sdk/opencodeClient'
import CordovaUtils from './utils/CordovaUtils'
import markdownit from 'markdown-it'

export default {
  name: 'AiChatPanel',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '智能助手'
    },
    subtitle: {
      type: String,
      default: '随时提问，一键生成建议'
    },
    placeholder: {
      type: String,
      default: '请输入你想让 AI 帮你处理的问题...'
    },
    initialMessages: {
      type: Array,
      default: function () {
        return []
      }
    },
    // 新增：OpenCode 服务器配置
    opencodeUrl: {
      type: String,
      default: '/opencode'
    },
    opencodeDirectory: {
      type: String,
      default: '/home/opencode/app'
    },
    opencodeSystem: {
      type: String,
      default: ''
    },
    opencodeAgent: {
      type: String,
      default: 'build'
    },
    opencodeModelID: {
      type: String,
      default: 'GLM-5'
    },
    opencodeProviderID: {
      type: String,
      default: 'jdprovider'
    },
    /** 自定义轻提示；不传则尝试 Mint UI this.$toast */
    toast: {
      type: Function,
      default: undefined
    },
    /** 自定义确认框，返回 Promise<boolean>；不传则 Mint this.$messagebox.confirm，再无则 window.confirm */
    confirm: {
      type: Function,
      default: undefined
    },
    // 新增:会话来源标识
    source: {
      type: String,
      default: 'crm'
    },
    userId: {
      type: String,
      default: ''
    },
    maxSessionCount: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      aiInput: '',
      aiMessages: [],
      textareaMinHeight: 40,
      textareaMaxHeight: 138,
      isComposingInput: false,
      /** 各会话是否仍在生成中（可离开当前页后再回来仍显示「思考中」） */
      activeGenerationBySession: {},
      /** 各会话是否已出现可展示的 text 流（用于与「仅点点」的区分） */
      hasReplyContentBySession: {},
      /** 非当前查看会话的 part 类型，供后台 part.delta 过滤 */
      streamPartTypesBySession: {},
      streamFinalized: false,
      sessionId: null, // 当前会话ID
      opencodeClient: null, // OpenCode 客户端实例
      showSessionDrawer: false,
      sessionList: [],
      sseAbortController: null,
      activeAssistantMessageIndex: -1,
      activeAssistantText: '',
      streamingAssistantMessageIds: [],
      streamingUserMessageIds: [],
      assistantPartTextMap: {},
      assistantPartOrder: [],
      userInitiatedStop: false,
      /** 流式 part 类型，与 formatMessages 仅展示 text 对齐，用于 part.delta 上过滤 */
      streamPartIdTypes: {},
      /** 为拦截侧滑/系统返回触发的 history.back 而多压的一条，与 Cordova backbutton 无关 */
      aiPanelHistoryPushed: false,
      /** 检查是否为该条会话的第一条信息 */
      isFirstUserMessage: false,
      md: markdownit({
         breaks: true,
        // linkify: true,
        html: false
      })
    }
  },
  computed: {
    sessionIdListStorageKey() {
      return `opencodeSessionList_${this.source}`
    },
    lastActiveSessionIdKey() {
      return `opencodeLastActiveSessionId_${this.source}`
    },
    isCurrentSessionLoading() {
      return !!(this.sessionId && this.activeGenerationBySession[this.sessionId])
    },
    hasCurrentSessionReplyContent() {
      return !!(this.sessionId && this.hasReplyContentBySession[this.sessionId])
    }
  },
  async mounted() {
    await this.initOpencodeClient()
  },
  watch: {
    visible(val, oldVal) {
      if (oldVal === true && val === false) {
        this.$emit('close')
      }
      if (val) {
        if (!this.aiMessages.length) {
          this.aiMessages = JSON.parse(JSON.stringify(this.initialMessages))
        }
        this.$nextTick(() => {
          this.scrollToBottom()
          this.autoResizeTextarea()
        })
        // Cordova 物理/左侧返回键走 global customBackHistory；否则不会关闭浮层、也不会触发 @close
        CordovaUtils.setCustomBackForOverlay(() => {
          if (this.showSessionDrawer) {
            this.closeSessionDrawer()
            return
          }
          this.goBack()
        })
        this.$nextTick(() => {
          this._installHistoryBackForSwipe()
        })
      } else {
        this.showSessionDrawer = false
        CordovaUtils.clearCustomBackForOverlay()
        this._teardownHistoryBackForSwipe()
      }
    },
    aiInput() {
      this.$nextTick(() => {
        this.autoResizeTextarea()
      })
    }
  },
  methods: {
    /**
     * 轻提示：优先 props.toast，其次 this.$toast。
     * @param {string|{message:string,position?:string}} payload
     * @returns {boolean} 是否已交给某类 toast 展示（未展示时宿主可用文案兜底）
     */
     showToast(payload) {
      const opts = typeof payload === 'string' ? { message: payload } : (payload || {})
      const message = opts.message
      if (!message) {
        return false
      }
      if (typeof this.toast === 'function') {
        this.toast(opts)
        return true
      }
      if (typeof this.$toast === 'function') {
        this.$toast({
          message,
          position: opts.position || 'bottom'
        })
        return true
      }
      try {
        window.alert(message)
        return true
      } catch (e) {
        return false
      }
    },
    /**
     * 确认框：优先 props.confirm，其次 this.$messageBox，最后 window.confirm。
     * @param {{message?:string,title?:string}} options
     */
    async confirmDelete(options = {}) {
      const message =
        options.message != null ? options.message : '确定删除该会话？'
      const title = options.title != null ? options.title : ''

      if (typeof this.confirm === 'function') {
        try {
          return !!(await this.confirm({ message, title }))
        } catch (e) {
          return false
        }
      }

      const messageBox = this.$messageBox
      if (messageBox && typeof messageBox.confirm === 'function') {
        try {
          const result = await messageBox.confirm(message, title)
          if (typeof result === 'string') {
            return result === 'confirm'
          }
          return result !== false
        } catch (e) {
          return false
        }
      }

      try {
        return window.confirm(message)
      } catch (e) {
        return false
      }
    },
    /**
     * 只创建 SDK 与全局 SSE。不在此创建服务端会话。
     */
    ensureOpenCodeClient() {
      if (this.opencodeClient) return
      this.opencodeClient = createOpencodeClient({
        baseUrl: this.opencodeUrl,
        parseAs: 'json',
        directory: this.opencodeDirectory
        // token: this.axios && this.axios.globalToken,
        // deviceId: this.axios && this.axios.globalDeviceId
      })
      // // console.log("opencodeClient", this.opencodeClient);

      this.startGlobalEventLoop()
    },
    readLastActiveSessionId() {
      try {
        const raw = localStorage.getItem(this.lastActiveSessionIdKey)
        if (!raw) return null
        const map = JSON.parse(raw) || {}
        return map[this.userId] || null
      } catch (e) {
        return null
      }
    },
    persistLastActiveSessionId(id) {
      if (!id || !this.userId) return
      try {
        const key = this.lastActiveSessionIdKey
        const map = JSON.parse(localStorage.getItem(key)) || {}
        map[this.userId] = id
        localStorage.setItem(key, JSON.stringify(map))
      } catch (e) {
        // // console.log("persistLastActiveSessionId", e);
      }
    },
    clearLastActiveSessionId() {
      if (!this.userId) return
      try {
        const key = this.lastActiveSessionIdKey
        const map = JSON.parse(localStorage.getItem(key)) || {}
        if (map[this.userId] == null) return
        delete map[this.userId]
        localStorage.setItem(key, JSON.stringify(map))
      } catch (e) {
        // // console.log("clearLastActiveSessionId", e);
      }
    },
    normalizeStoredSessionItem(item) {
      if (!item) return null
      if (typeof item === 'string') {
        return {
          sessionId: item,
          title: ''
        }
      }
      if (typeof item === 'object') {
        const sessionId = item.sessionId || item.id || ''
        if (!sessionId) return null
        return {
          sessionId,
          title: item.title ? String(item.title).trim() : ''
        }
      }
      return null
    },
    normalizeStoredSessionList(list) {
      if (!Array.isArray(list)) return []
      const uniqueMap = {}
      const sessions = []
      for (const item of list) {
        const normalized = this.normalizeStoredSessionItem(item)
        if (!normalized || !normalized.sessionId || uniqueMap[normalized.sessionId]) continue
        uniqueMap[normalized.sessionId] = true
        sessions.push(normalized)
      }
      return sessions
    },
    getStoredSessions() {
      if (!this.userId) return []
      try {
        const map = JSON.parse(localStorage.getItem(this.sessionIdListStorageKey)) || {}
        return this.normalizeStoredSessionList(map[this.userId])
      } catch (e) {
        // // console.log('getStoredSessions', e)
      }
      return []
    },
    persistSessionsToStorage(sessions) {
      if (!this.userId) return
      try {
        const key = this.sessionIdListStorageKey
        const map = JSON.parse(localStorage.getItem(key)) || {}
        map[this.userId] = this.normalizeStoredSessionList(sessions).map(item => ({
          sessionId: item.sessionId,
          title: item.title || ''
        }))
        localStorage.setItem(key, JSON.stringify(map))
      } catch (e) {
        // // console.log('persistSessionsToStorage', e)
      }
    },
    applySessionListFromStoredSessions(sessions) {
      this.sessionList = (sessions || []).map(item => ({
        id: item.sessionId,
        title: item.title || ''
      }))
    },
    /** 进入页面：空对话、无 session，仅使用本地会话列表供侧栏切换 */
    prepareEmptyEntryView(shouldRefreshSessionList = true) {
      this.resetStreamingUiStateForViewChange()
      this.sessionId = null
      this.aiInput = ''
      if (shouldRefreshSessionList) {
        this.refreshSessionListFromStorage()
      }
      this.aiMessages = JSON.parse(JSON.stringify(this.initialMessages))
      this.closeSessionDrawer()
      this.$nextTick(() => {
        this.scrollToBottom()
        this.autoResizeTextarea()
      })
    },
    async initOpencodeClient() {
      try {
        this.ensureOpenCodeClient()
        this.prepareEmptyEntryView()
      } catch (error) {
        console.error('初始化 OpenCode 客户端失败:', error)
        this.addSystemMessage('无法连接到 AI 服务，请检查服务是否已启动')
      }
    },
    /** 首条用户消息发出时向服务端创建会话 */
    async createServerSessionForFirstSend() {
      this.ensureOpenCodeClient()
      if (!this.opencodeClient) return false
      try {
        const result = await this.opencodeClient.session.create({
          directory: this.opencodeDirectory
        })
        if (result && result.data && result.data.id) {
          this.sessionId = result.data.id
          this.persistLastActiveSessionId(this.sessionId)
          const title = result.data.title
            ? String(result.data.title).trim()
            : ('新会话-' + this.formatDate())
          this.ensureSessionInList(this.sessionId, title)
          return true
        }
      } catch (e) {
        console.error('创建会话失败:', e)
        if (!this.showToast({ message: '创建会话失败，请稍后重试', position: 'bottom' })) {
          this.addSystemMessage('创建会话失败，请稍后重试。')
        }
      }
      return false
    },
    /** 侧栏「新建会话」：仅本地空对话，不请求服务端 */
    createNewSession() {
      this.ensureOpenCodeClient()
      this.clearLastActiveSessionId()
      this.prepareEmptyEntryView(false)
    },
    formatDate(date = new Date()) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`
    },
    ensureSessionInList(sessionId, title) {
      if (!sessionId) return
      let userSessions = this.getStoredSessions()
      const normalizedTitle = title ? String(title).trim() : ''
      const existsIndex = userSessions.findIndex(item => item.sessionId === sessionId)
      let nextItem = null
      if (existsIndex !== -1) {
        const removed = userSessions.splice(existsIndex, 1)
        if (removed && removed[0]) {
          nextItem = removed[0]
        }
      }
      nextItem = {
        sessionId,
        title: normalizedTitle || (nextItem && nextItem.title) || ('新会话-' + this.formatDate())
      }
      userSessions.unshift(nextItem)
      if (userSessions.length > this.maxSessionCount) {
        userSessions = userSessions.slice(0, 10)
      }
      this.persistSessionsToStorage(userSessions)
      this.applySessionListFromStoredSessions(userSessions)
    },
    refreshSessionListFromStorage() {
      const sessions = this.getStoredSessions()
      this.applySessionListFromStoredSessions(sessions)
    },
    async syncSessionTitleFromServer(sessionId, retry = 0) {
      if (!this.opencodeClient || !sessionId) return false
      try {
        const result = await this.opencodeClient.session.get({
          sessionID: sessionId
        })
        const title = result && result.data && result.data.title
          ? String(result.data.title).trim()
          : ''
        // // console.log('title', title)
        if (title) {
          return this.updateSessionTitle(sessionId, title)
        }
      } catch (error) {
        console.warn('获取会话标题失败:', error)
      }
      if (retry < 2) {
        await new Promise(resolve => setTimeout(resolve, 1000))
        return this.syncSessionTitleFromServer(sessionId, retry + 1)
      }
      return false
    },
    getSessionTitle(session) {
      if (!session) return '未命名会话'
      return session.title || '未命名会话'
    },
    async toggleSessionDrawer() {
      this.showSessionDrawer = !this.showSessionDrawer
    },
    closeSessionDrawer() {
      this.showSessionDrawer = false
    },
    /**
     * 切换/新建会话视图时重置**当前视图的**合流与缓冲。不按会话清 activeGenerationBySession，
     * 以便其他会话在后台仍生成、切回时仍能显示「正在思考」/停止态。
     */
    resetStreamingUiStateForViewChange() {
      this.streamFinalized = true
      this.userInitiatedStop = false
      this.activeAssistantMessageIndex = -1
      this.activeAssistantText = ''
      this.streamingAssistantMessageIds = []
      this.streamingUserMessageIds = []
      this.assistantPartTextMap = {}
      this.assistantPartOrder = []
      this.streamPartIdTypes = {}
    },
    async switchSession(session) {
      if (!session || !session.id) return
      this.resetStreamingUiStateForViewChange()
      this.sessionId = session.id
      this.aiInput = ''
      await this.loadSessionMessages(session.id)
      this.persistLastActiveSessionId(session.id)
      this.closeSessionDrawer()
      this.$nextTick(() => {
        this.scrollToBottom()
        this.autoResizeTextarea()
      })
    },
    async deleteSession(session) {
      if (!session || !session.id) return
      const ok = await this.confirmDelete({
        message: '确定删除该会话？',
        title: ''
      })
      if (!ok) return
      this.performDeleteSession(session)
    },
    async performDeleteSession(session) {
      if (!session || !session.id) return
      try {
        if (this.opencodeClient) {
          await this.opencodeClient.session.delete({ sessionID: session.id })
        }
      } catch (error) {
        console.error('删除远程会话失败:', error)
      }
      const wasCurrent = this.sessionId === session.id
      const userSessions = this.getStoredSessions().filter(item => item.sessionId !== session.id)
      this.persistSessionsToStorage(userSessions)
      this.applySessionListFromStoredSessions(userSessions)
      if (session.id === this.readLastActiveSessionId()) {
        this.clearLastActiveSessionId()
      }

      if (!wasCurrent) return
      if (this.sessionList.length > 0) {
        await this.switchSession(this.sessionList[0])
      } else {
        this.clearLastActiveSessionId()
        this.resetStreamingUiStateForViewChange()
        this.sessionId = null
        this.aiMessages = JSON.parse(JSON.stringify(this.initialMessages))
        this.$nextTick(() => {
          this.scrollToBottom()
          this.autoResizeTextarea()
        })
      }
    },
    async loadSessionMessages(sessionId) {
      try {
        const messagesResult = await this.opencodeClient.session.messages({
          sessionID: sessionId,
          directory: this.opencodeDirectory
        })
        // // console.log('消息列表响应:', messagesResult)
        // // console.log('消息数据:', messagesResult.data)

        const messages = this.formatMessages(messagesResult.data)
        // // console.log('格式化后的消息:', messages)

        if (messages.length > 0) {
          this.aiMessages = messages
        } else {
          this.aiMessages = JSON.parse(JSON.stringify(this.initialMessages))
        }

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (error) {
        console.error('加载会话消息失败:', error)
        this.aiMessages = JSON.parse(JSON.stringify(this.initialMessages))
      }
    },
    /**
     * 与界面标题「思考过程」一致：去掉 [思考过程] 前缀/单独标题行、规范「思 考 过 程」间空白，段落仅保留 \n\n
     */
    normalizeReasoningPlain(text) {
      if (text == null || text === '') return ''
      let s = String(text).replace(/\r\n/g, '\n')
      s = s.replace(/^\[思考[\s\u3000\u00a0]*过程\][\s\u3000\u00a0]*/m, '')
      s = s.replace(/(^|\n)[\s\u3000\u00a0]*思考[\s\u3000\u00a0]+过程[\s\u3000\u00a0]*(?=\n|$)/g, '\n')
      s = s.replace(/思考[\s\u3000\u00a0]+过程/g, '思考过程')
      s = s.replace(/\n{3,}/g, '\n\n')
      return s.trim()
    },
    formatMessages(messagesData) {
      if (!Array.isArray(messagesData) || messagesData.length === 0) {
        return []
      }

      const formattedMessages = []

      for (const msg of messagesData) {
        // 跳过没有 parts 的消息
        if (!msg.parts || !Array.isArray(msg.parts)) {
          continue
        }

        let content = ''
        const reasoningPartTexts = []

        for (const part of msg.parts) {
          if (part.type === 'text') {
            content += part.text || ''
          } else if (part.type === 'reasoning') {
            reasoningPartTexts.push(part.text != null ? String(part.text) : '')
          }
        }

        const reasoning = reasoningPartTexts.filter((t) => t && t.trim()).join('\n\n')
        const cTrim = content.trim()
        const rNorm = this.normalizeReasoningPlain(reasoning)
        const rTrim = rNorm.trim()
        if (cTrim || rTrim) {
          formattedMessages.push({
            role: msg.info.role,
            content: cTrim || '',
            reasoning: (msg.info.role === 'assistant' && rTrim) ? rTrim : '',
            timestamp: msg.info.time && msg.info.time.created ? msg.info.time.created : null,
            messageId: msg.info.id
          })
        }
      }

      return this.mergeConsecutiveAssistantBubbles(formattedMessages)
    },
    /**
     * 接口常把多段思考拆成连续多条 assistant，合并为一条，只出现一块「思考过程」
     * （思考正文多段用空行拼接；最终答案取同组内带正文的最后一条）
     */
    mergeConsecutiveAssistantBubbles(list) {
      if (!list || list.length === 0) {
        return list
      }
      const out = []
      let i = 0
      while (i < list.length) {
        const m = list[i]
        if (m.role !== 'assistant') {
          out.push(m)
          i++
          continue
        }
        const group = []
        while (i < list.length && list[i].role === 'assistant') {
          group.push(list[i])
          i++
        }
        if (group.length === 1) {
          out.push(group[0])
          continue
        }
        const reasonPlainParts = group
          .map((g) => this.normalizeReasoningPlain(this.messageHtmlToPlain(g.reasoning)))
          .filter(Boolean)
        const mergedReasoning = reasonPlainParts.length
          ? this.normalizeReasoningPlain(reasonPlainParts.join('\n\n'))
          : ''
        let mergedContent = ''
        for (let k = group.length - 1; k >= 0; k--) {
          if (this.messageHtmlToPlain(group[k].content)) {
            mergedContent = group[k].content
            break
          }
        }
        const last = group[group.length - 1]
        out.push({
          ...last,
          reasoning: mergedReasoning,
          content: mergedContent || '',
          messageId: last.messageId
        })
      }
      return out
    },
    goBack() {
      if (this.aiPanelHistoryPushed) {
        try {
          window.history.back()
        } catch (e) {
          this.$emit('update:visible', false)
        }
      } else {
        this.$emit('update:visible', false)
      }
    },
    _installHistoryBackForSwipe() {
      if (this.aiPanelHistoryPushed) {
        return
      }
      this._onAiPanelPopState = this._onAiPanelPopState.bind(this)
      try {
        window.history.pushState({ __aiChatPanel: true }, '', window.location.href)
        this.aiPanelHistoryPushed = true
        window.addEventListener('popstate', this._onAiPanelPopState, false)
      } catch (e) {
        this.aiPanelHistoryPushed = false
      }
    },
    _onAiPanelPopState() {
      if (!this.aiPanelHistoryPushed) {
        return
      }
      if (this.showSessionDrawer) {
        this.closeSessionDrawer()
        try {
          window.history.pushState({ __aiChatPanel: true }, '', window.location.href)
        } catch (e) {
          // // console.log("_onAiPanelPopState", e);
        }
        return
      }
      this.aiPanelHistoryPushed = false
      window.removeEventListener('popstate', this._onAiPanelPopState, false)
      if (this.visible) {
        this.$emit('update:visible', false)
      }
    },
    _teardownHistoryBackForSwipe() {
      if (!this.aiPanelHistoryPushed) {
        return
      }
      this.aiPanelHistoryPushed = false
      window.removeEventListener('popstate', this._onAiPanelPopState, false)
      try {
        window.history.back()
      } catch (e) {
        // // console.log("_teardownHistoryBackForSwipe", e);

      }
    },
    onEnterKey(e) {
      if (!e) return
      if (this.isComposingInput || e.isComposing || e.keyCode === 229) return
      // Shift+Enter 换行；Enter 发送
      if (e.shiftKey) return
      e.preventDefault()
      this.sendMessage()
    },
    onCompositionStart() {
      this.isComposingInput = true
    },
    onCompositionEnd() {
      this.isComposingInput = false
    },
    autoResizeTextarea() {
      let el = this.$refs.aiTextarea
      if (!el) return
      el.style.height = 'auto'
      const style = window.getComputedStyle(el)
      const borderTop = parseFloat(style.borderTopWidth) || 0
      const borderBottom = parseFloat(style.borderBottomWidth) || 0
      const boxSizing = style.boxSizing
      // scrollHeight 包含 padding，不含 border；而我们 textarea 使用 border-box
      // 需要把 border 也算进 height，避免 1-2px 误差导致提前出现滚动条
      let next = el.scrollHeight + (boxSizing === 'border-box' ? (borderTop + borderBottom) : 0)
      if (next < this.textareaMinHeight) {
        next = this.textareaMinHeight
      }
      const reachedMax = next >= this.textareaMaxHeight
      if (next > this.textareaMaxHeight) next = this.textareaMaxHeight
      el.style.height = next + 'px'
      el.style.overflowY = reachedMax ? 'auto' : 'hidden'
    },

    // 更新会话标题（本地存储 + 当前列表）
    async updateSessionTitle(sessionId, title) {
      if (!sessionId) return false
      const normalizedTitle = title ? String(title).trim() : ''
      if (!normalizedTitle) return false
      const sessions = this.getStoredSessions()
      const sessionIndex = sessions.findIndex(item => item.sessionId === sessionId)
      if (sessionIndex === -1) {
        return false
      }
      sessions[sessionIndex] = {
        ...sessions[sessionIndex],
        title: normalizedTitle
      }
      this.persistSessionsToStorage(sessions)
      this.applySessionListFromStoredSessions(sessions)
      return true
    },

    // 新增：根据用户消息生成标题
    generateTitleFromMessage(message) {
      // 移除多余空格和换行
      let title = message.trim().replace(/\s+/g, ' ')
      // 限制长度，最多30个字符
      if (title.length > 30) {
        title = title.substring(0, 27) + '...'
      }
      let messageTitle = (title || '新对话') + ' - ' + this.formatDate()
      return messageTitle
    },

    async sendMessage() {
      let message = this.aiInput && this.aiInput.trim()
      if (!message) return
      if (this.isCurrentSessionLoading) return

      this.ensureOpenCodeClient()
      if (!this.opencodeClient) {
        this.addSystemMessage('无法连接 AI 服务，请稍后再试')
        return
      }

      if (!this.sessionId) {
        const created = await this.createServerSessionForFirstSend()
        if (!created) return
      }
      // 每次发送都把当前会话置顶，保证本地会话列表按最近消息排序
      this.ensureSessionInList(this.sessionId)
      // 检查是否是第一条用户消息
      this.isFirstUserMessage = this.aiMessages.filter(m => m.role === 'user').length === 0

      this.aiMessages.push({
        role: 'user',
        content: message
      })
      this.aiInput = ''
      this.$set(this.activeGenerationBySession, this.sessionId, true)
      this.$set(this.hasReplyContentBySession, this.sessionId, false)
      this.streamFinalized = false
      this.userInitiatedStop = false
      this.$nextTick(() => {
        this.scrollToBottom()
        this.autoResizeTextarea()
      })
      try {
        this.activeAssistantMessageIndex = -1
        this.activeAssistantText = ''
        this.streamingAssistantMessageIds = []
        this.streamingUserMessageIds = []
        this.assistantPartTextMap = {}
        this.assistantPartOrder = []
        this.streamPartIdTypes = {}
        const body = {
          agent: this.opencodeAgent,
          parts: [{ type: 'text', text: message }],
          model: {
            providerID: this.opencodeProviderID,
            modelID: this.opencodeModelID
          },
          system: this.opencodeSystem
        }
        // 触发异步任务，真实增量由 global.event 推送
        this.opencodeClient.session.promptAsync({
          sessionID: this.sessionId,
          ...body
        }).catch((error) => {
          console.error('promptAsync 触发失败:', error)
          this.markStreamFailed(error)
        })

        this.$emit('send', { message, response: '' })
      } catch (error) {
        console.error('发送消息失败:', error)
        this.markStreamFailed(error)
      }
    },
    ensureActiveAssistantBubble() {
      if (this.activeAssistantMessageIndex >= 0 && this.aiMessages[this.activeAssistantMessageIndex]) return
      this.activeAssistantMessageIndex = this.aiMessages.push({
        role: 'assistant',
        content: '',
        reasoning: ''
      }) - 1
    },
    appendAssistantDelta(delta) {
      if (!delta) return
      let normalizedDelta = delta
      if (!this.activeAssistantText) {
        normalizedDelta = String(delta).replace(/^\s+/, '')
      }
      if (!normalizedDelta) return
      if (this.sessionId) {
        this.$set(this.hasReplyContentBySession, this.sessionId, true)
      }
      this.ensureActiveAssistantBubble()
      this.activeAssistantText += normalizedDelta
      if (this.aiMessages[this.activeAssistantMessageIndex]) {
        // 与 formatMessages 一致：存纯文本，展示层用 md.render（避免 formatResponse 的 HTML 再被 md 转义成可见标签）
        this.aiMessages[this.activeAssistantMessageIndex].content = this.activeAssistantText
      }
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    upsertAssistantPart(partID, text, appendMode) {
      if (!partID) return
      if (this.assistantPartOrder.indexOf(partID) === -1) {
        this.assistantPartOrder.push(partID)
      }
      const current = this.assistantPartTextMap[partID] || ''
      let next = appendMode ? (current + (text || '')) : (text || '')
      if (!current) {
        next = String(next).replace(/^\s+/, '')
      }
      this.$set(this.assistantPartTextMap, partID, next)
      this.rebuildAssistantMessageFromPartMaps()
    },
    /**
     * 按 part 的 text / reasoning 类型分别合并，流式时与历史 formatMessages 展示一致
     */
    rebuildAssistantMessageFromPartMaps() {
      const textChunks = []
      const reasoningChunks = []
      for (const id of this.assistantPartOrder) {
        const t = (id && this.streamPartIdTypes[id]) || 'text'
        const chunk = this.assistantPartTextMap[id] != null
          ? String(this.assistantPartTextMap[id])
          : ''
        if (t === 'reasoning') {
          reasoningChunks.push(chunk)
        } else {
          textChunks.push(chunk)
        }
      }
      const reasoningRaw = reasoningChunks.join('\n\n')
      const textRaw = textChunks.join('\n')
      this.activeAssistantText = textRaw
      if (this.sessionId && (reasoningRaw.trim() || textRaw.trim())) {
        this.$set(this.hasReplyContentBySession, this.sessionId, true)
      }
      this.ensureActiveAssistantBubble()
      const m = this.aiMessages[this.activeAssistantMessageIndex]
      if (m) {
        const rN = this.normalizeReasoningPlain(reasoningRaw.trim())
        m.reasoning = rN || ''
        m.content = textRaw.trim() ? textRaw.trim() : ''
      }
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    replaceAssistantContent(text) {
      this.ensureActiveAssistantBubble()
      let nextText = text || ''
      if (!this.activeAssistantText) {
        nextText = String(nextText).replace(/^\s+/, '')
      }
      this.activeAssistantText = nextText
      if (this.activeAssistantText && this.sessionId) {
        this.$set(this.hasReplyContentBySession, this.sessionId, true)
      }
      if (this.aiMessages[this.activeAssistantMessageIndex]) {
        this.aiMessages[this.activeAssistantMessageIndex].content = this.activeAssistantText
      }
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    markStreamDone() {
      if (this.streamFinalized) return
      this.streamFinalized = true
      if (this.sessionId) {
        this.$set(this.activeGenerationBySession, this.sessionId, false)
        this.$set(this.hasReplyContentBySession, this.sessionId, false)
        this.$delete(this.streamPartTypesBySession, this.sessionId)
      }
      this.activeAssistantMessageIndex = -1
      this.activeAssistantText = ''
      this.streamingAssistantMessageIds = []
      this.streamingUserMessageIds = []
      this.assistantPartTextMap = {}
      this.assistantPartOrder = []
      this.streamPartIdTypes = {}
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    /**
     * 与 formatMessages 一致：展示 text 与 reasoning
     */
    isStreamVisiblePartType(type) {
      return type === 'text' || type === 'reasoning'
    },
    findMessageIndexById(messageID) {
      if (!messageID) return -1
      return this.aiMessages.findIndex(message => message && message.messageId === messageID)
    },
    bindPendingUserMessageId(messageID) {
      if (!messageID || !this.aiMessages.length) return -1
      const lastIndex = this.aiMessages.length - 1
      const lastMessage = this.aiMessages[lastIndex]
      if (lastMessage && lastMessage.role === 'user' && !lastMessage.messageId) {
        this.$set(lastMessage, 'messageId', messageID)
        return lastIndex
      }
      return -1
    },
    ensureUserMessageBubble(messageID, initialText = '') {
      let index = this.findMessageIndexById(messageID)
      if (index !== -1) {
        return index
      }
      index = this.bindPendingUserMessageId(messageID)
      if (index !== -1) {
        const message = this.aiMessages[index]
        if (message && !message.content && initialText) {
          message.content = initialText
        }
        return index
      }
      return this.aiMessages.push({
        role: 'user',
        content: initialText || '',
        messageId: messageID
      }) - 1
    },
    appendUserDelta(messageID, delta) {
      if (!messageID || !delta) return
      const index = this.ensureUserMessageBubble(messageID)
      const message = this.aiMessages[index]
      if (!message) return
      const current = message.content || ''
      let next = current + delta
      if (!current) {
        next = String(next).replace(/^\s+/, '')
      }
      message.content = next
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    replaceUserContent(messageID, text) {
      if (!messageID) return
      const index = this.ensureUserMessageBubble(messageID)
      const message = this.aiMessages[index]
      if (!message) return
      let next = text || ''
      if (!(message.content || '').trim()) {
        next = String(next).replace(/^\s+/, '')
      }
      message.content = next
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    /** 用户停止或服务端因 abort 结束流；此类情况不展示「处理失败」类气泡 */
    isStreamAbortError(error) {
      if (!error) return false
      if (error.name === 'AbortError') return true
      const m = String(error.message != null ? error.message : error).trim()
      if (!m) return false
      const l = m.toLowerCase()
      return l === 'aborted'
    },
    /** 用户主动终止当前轮生成（对齐 OpenCode：POST /session/:id/abort） */
    markStreamAborted() {
      this.userInitiatedStop = false
      if (this.sessionId) {
        this.$set(this.activeGenerationBySession, this.sessionId, false)
        this.$set(this.hasReplyContentBySession, this.sessionId, false)
        this.$delete(this.streamPartTypesBySession, this.sessionId)
      }
      if (this.streamFinalized) {
        this.$nextTick(() => { this.scrollToBottom() })
        return
      }
      this.streamFinalized = true
      const lastMessage = this.aiMessages.length ? this.aiMessages[this.aiMessages.length - 1] : null
      if (lastMessage && lastMessage.role === 'assistant') {
        const plain = this.messageHtmlToPlain(lastMessage.content) + this.messageHtmlToPlain(lastMessage.reasoning)
        if (!plain.trim()) {
          lastMessage.content = '已停止生成'
        }
      }
      this.activeAssistantMessageIndex = -1
      this.activeAssistantText = ''
      this.streamingAssistantMessageIds = []
      this.streamingUserMessageIds = []
      this.assistantPartTextMap = {}
      this.assistantPartOrder = []
      this.streamPartIdTypes = {}
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    async abortRunningSession() {
      if (!this.isCurrentSessionLoading) return
      this.userInitiatedStop = true
      if (this.opencodeClient && this.sessionId) {
        try {
          await this.opencodeClient.session.abort({
            sessionID: this.sessionId
          })
        } catch (error) {
          console.error('停止生成失败:', error)
          this.showToast({
            message: (error && error.message) || '停止失败，请稍后重试',
            position: 'bottom'
          })
        }
      }
      this.markStreamAborted()
      this.$emit('abort')
    },
    markStreamFailed(error) {
      // // console.log(error)
      if (this.streamFinalized) return
      if (this.userInitiatedStop || this.isStreamAbortError(error)) {
        this.markStreamAborted()
        return
      }
      this.streamFinalized = true
      if (this.sessionId) {
        this.$set(this.activeGenerationBySession, this.sessionId, false)
        this.$set(this.hasReplyContentBySession, this.sessionId, false)
        this.$delete(this.streamPartTypesBySession, this.sessionId)
      }
      const lastMessage = this.aiMessages.length ? this.aiMessages[this.aiMessages.length - 1] : null
      if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
        lastMessage.content = '抱歉，处理你的请求时出现了问题。请稍后重试。<br>错误信息：' + error.message
      } else {
        this.aiMessages.push({
          role: 'assistant',
          content: '抱歉，处理你的请求时出现了问题。请稍后重试。<br>错误信息：' + error.message
        })
      }
      this.activeAssistantMessageIndex = -1
      this.activeAssistantText = ''
      this.streamingAssistantMessageIds = []
      this.streamingUserMessageIds = []
      this.assistantPartTextMap = {}
      this.assistantPartOrder = []
      this.streamPartIdTypes = {}
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    normalizeDir(dir) {
      return String(dir || '')
        .replace(/\\/g, '/')
        .replace(/\/+$/, '')
    },
    unwrapGlobalEventRow(row) {
      const payload = row && row.data !== undefined ? row.data : row
      if (payload && payload.payload) {
        return {
          event: payload.payload,
          directory: payload.directory
        }
      }
      return {
        event: payload,
        directory: payload && payload.directory ? payload.directory : null
      }
    },
    /**
     * 非当前所查看会话上的事件：只维护该会话的「已出字」等侧栏状态，不写当前 aiMessages
     */
    onBackgroundSessionEvent(ev, sessionID) {
      if (ev.type === 'message.part.delta') {
        const messageID = ev.properties.messageID || (ev.properties.part && ev.properties.part.messageID)
        if (messageID && this.streamingUserMessageIds.indexOf(messageID) !== -1) {
          return
        }
        const p = ev.properties.part || {}
        const partID = ev.properties.partID || p.id
        let typesMap = this.streamPartTypesBySession[sessionID]
        if (!typesMap) {
          this.$set(this.streamPartTypesBySession, sessionID, {})
          typesMap = this.streamPartTypesBySession[sessionID]
        }
        if (p.id && p.type) {
          this.$set(typesMap, p.id, p.type)
        } else if (partID && p.type) {
          this.$set(typesMap, partID, p.type)
        }
        const partType = p.type || (partID && typesMap[partID])
        if (partType && !this.isStreamVisiblePartType(partType)) {
          return
        }
        const deltaText = ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || ''
        if (String(deltaText).trim()) {
          this.$set(this.hasReplyContentBySession, sessionID, true)
        }
        return
      }
      if (ev.type === 'message.part.update' || ev.type === 'message.part.updated') {
        const part = ev.properties.part || {}
        let typesMap = this.streamPartTypesBySession[sessionID]
        if (!typesMap) {
          this.$set(this.streamPartTypesBySession, sessionID, {})
          typesMap = this.streamPartTypesBySession[sessionID]
        }
        if (part.id && part.type) {
          this.$set(typesMap, part.id, part.type)
        }
        if (!part.type || !this.isStreamVisiblePartType(part.type)) {
          return
        }
        if ((part.text || '').trim()) {
          this.$set(this.hasReplyContentBySession, sessionID, true)
        }
      }
    },
    handleGlobalEvent(ev) {
      // // console.log(ev)
      if (!ev || !ev.type || !ev.properties) return
      const sessionID = ev.properties.sessionID || (ev.properties.info && ev.properties.info.sessionID) || (ev.properties.part && ev.properties.part.sessionID)

      if (ev.type === 'session.idle') {
        if (sessionID) {
          this.$set(this.activeGenerationBySession, sessionID, false)
          this.$set(this.hasReplyContentBySession, sessionID, false)
          this.$delete(this.streamPartTypesBySession, sessionID)
        }
        if (!sessionID || sessionID === this.sessionId) {
          this.markStreamDone()
        }
        if (this.isFirstUserMessage && this.sessionId) {
          // // console.log('syncSessionTitleFromServer', this.sessionId)
          this.syncSessionTitleFromServer(this.sessionId)
          this.isFirstUserMessage = false
        }
        return
      }

      if (ev.type === 'session.error') {
        if (sessionID) {
          this.$set(this.activeGenerationBySession, sessionID, false)
          this.$set(this.hasReplyContentBySession, sessionID, false)
          this.$delete(this.streamPartTypesBySession, sessionID)
        }
        if (sessionID && sessionID !== this.sessionId) {
          return
        }
        // // console.log(ev.type)
        // // console.log(ev)
        let msg = '会话流式处理失败'
        const err = ev.properties.error
        if (typeof err === 'string') {
          msg = err
        } else if (err && err.message) {
          msg = err.message
        } else if (ev.properties.message) {
          msg = ev.properties.message
        } else if (ev.properties.error) {
          msg = ev.properties.error.data.message
        }
        if (this.isStreamAbortError(new Error(String(msg || ''))) || this.userInitiatedStop) {
          this.markStreamAborted()
          return
        }
        this.markStreamFailed(new Error(msg))
        return
      }

      if (sessionID && sessionID !== this.sessionId) {
        this.onBackgroundSessionEvent(ev, sessionID)
        return
      }

      if (ev.type === 'message.update' || ev.type === 'message.updated') {
        const role = ev.properties.role || (ev.properties.info && ev.properties.info.role)
        const messageID = ev.properties.messageID || ev.properties.id || (ev.properties.info && ev.properties.info.id)
        if (role === 'assistant' && messageID) {
          if (this.streamingAssistantMessageIds.indexOf(messageID) === -1) {
            this.streamingAssistantMessageIds.push(messageID)
          }
        } else if (role === 'user' && messageID) {
          this.bindPendingUserMessageId(messageID)
          if (this.streamingUserMessageIds.indexOf(messageID) === -1) {
            this.streamingUserMessageIds.push(messageID)
          }
        }
        return
      }

      if (ev.type === 'message.part.delta') {
        const messageID = ev.properties.messageID || (ev.properties.part && ev.properties.part.messageID)
        if (messageID && this.streamingUserMessageIds.indexOf(messageID) !== -1) {
          this.appendUserDelta(messageID, ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || '')
          return
        }
        if (messageID) {
          if (this.streamingAssistantMessageIds.indexOf(messageID) === -1) {
            if (this.sessionId && this.activeGenerationBySession[this.sessionId]) {
              this.streamingAssistantMessageIds.push(messageID)
            }
          }
        } else if (!this.streamingAssistantMessageIds.length) {
          if (!(this.sessionId && this.activeGenerationBySession[this.sessionId])) {
            return
          }
        }
        const p = ev.properties.part || {}
        const partID = ev.properties.partID || p.id
        if (p.id && p.type) {
          this.$set(this.streamPartIdTypes, p.id, p.type)
        } else if (partID && p.type) {
          this.$set(this.streamPartIdTypes, partID, p.type)
        }
        const partType = p.type || (partID && this.streamPartIdTypes[partID])
        if (partType && !this.isStreamVisiblePartType(partType)) {
          return
        }
        const deltaText = ev.properties.delta || (ev.properties.part && ev.properties.part.delta) || ''
        if (partID) {
          this.upsertAssistantPart(partID, deltaText, true)
        } else {
          this.appendAssistantDelta(deltaText)
        }
        return
      }

      if (ev.type === 'message.part.update' || ev.type === 'message.part.updated') {
        const part = ev.properties.part || {}
        const messageID = part.messageID || ev.properties.messageID
        if (messageID && this.streamingUserMessageIds.indexOf(messageID) !== -1) {
          this.replaceUserContent(messageID, part.text || '')
          return
        }
        if (messageID) {
          if (this.streamingAssistantMessageIds.indexOf(messageID) === -1) {
            if (this.sessionId && this.activeGenerationBySession[this.sessionId]) {
              this.streamingAssistantMessageIds.push(messageID)
            } else {
              return
            }
          }
        } else if (!this.streamingAssistantMessageIds.length) {
          if (!(this.sessionId && this.activeGenerationBySession[this.sessionId])) {
            return
          }
        }
        if (part.id) {
          this.$set(this.streamPartIdTypes, part.id, part.type)
        }
        if (!part.type || !this.isStreamVisiblePartType(part.type)) {
          return
        }
        if (part.id) {
          this.upsertAssistantPart(part.id, part.text || '', false)
        } else {
          this.replaceAssistantContent(part.text || '')
        }
      }
    },
    async startGlobalEventLoop() {
      // // console.log("startGlobalEventLoop--------------");

      if (!this.opencodeClient || this.sseAbortController) return
      const controller = new AbortController()
      // // console.log("controller", controller);

      this.sseAbortController = controller
      try {
        const { stream } = await this.opencodeClient.global.event({
          signal: controller.signal,
          onSseError: (err) => {
            console.error('SSE 错误:', err)
          }
        })
        // // console.log("stream", stream);

        for await (const row of stream) {
          // // console.log(row)
          const wrapped = this.unwrapGlobalEventRow(row)
          // // console.log(wrapped)
          // 部分服务端不会稳定回传 directory，避免误过滤导致收不到事件
          this.handleGlobalEvent(wrapped.event)
        }
      } catch (error) {
        if (!(error && error.name === 'AbortError')) {
          console.error('全局事件循环异常:', error)
        }
      } finally {
        this.sseAbortController = null
      }
    },
    // 从 OpenCode 响应中提取文本
    extractResponseText(responseData) {
      // 处理空响应
      if (!responseData || Object.keys(responseData).length === 0) {
        console.warn('收到空响应数据')
        return 'AI 服务未返回有效内容，请稍后重试。'
      }
      // 如果是数组（消息列表），取最后一条 assistant 消息
      if (Array.isArray(responseData)) {
        // 找到最后一条 assistant 消息
        const lastAssistantMsg = [...responseData]
          .reverse()
          .find(msg => msg.info && msg.info.role === 'assistant')
        if (lastAssistantMsg && lastAssistantMsg.parts) {
          const textParts = lastAssistantMsg.parts.filter(part => part.type === 'text')
          if (textParts.length > 0) {
            return textParts.map(part => part.text).join('\n')
          }
        }
        return '收到回复，但无法解析内容。'
      }
      // 如果是单条消息 { info, parts }
      if (responseData && responseData.parts) {
        const textParts = responseData.parts.filter(part => part.type === 'text')
        if (textParts.length > 0) {
          return textParts.map(part => part.text).join('\n')
        }
        // 检查推理内容
        const reasoningParts = responseData.parts.filter(part => part.type === 'reasoning')
        if (reasoningParts.length > 0) {
          return `[思考过程]\n${reasoningParts.map(part => part.text).join('\n')}`
        }
      }
      // 检查是否有错误
      if (responseData && responseData.info && responseData.info.error) {
        throw new Error(responseData.info.error.message || 'AI 响应出错')
      }
      return '收到回复，但无法解析内容。'
    },
    // 格式化响应文本（支持 HTML）
    formatResponse(text) {
      if (!text) return ''
      // 转换 markdown 风格的代码块
      let formatted = text.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
        return `<pre><code class="language-${lang}">${this.escapeHtml(code)}</code></pre>`
      })
      // 转换行内代码
      formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>')
      // 转换换行
      formatted = formatted.replace(/\n/g, '<br>')
      return formatted
    },
    // 转义 HTML
    escapeHtml(text) {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },
    // 添加系统消息
    addSystemMessage(content) {
      this.aiMessages.push({
        role: 'assistant',
        content: content
      })
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    scrollToBottom() {
      if (this.$refs.aiChatBody) {
        this.$refs.aiChatBody.scrollTop = this.$refs.aiChatBody.scrollHeight
      }
    },
    convertHtmlContent(string) {
      return this.md.render(string)
    },
    messageHtmlToPlain(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return (div.textContent || '').trim()
    },
    hasProcessParts(message) {
      return !!(message && Array.isArray(message.processParts) && message.processParts.length)
    },
    buildProcessStream(message) {
      if (!this.hasProcessParts(message)) return ''
      return message.processParts
        .map((part) => this.getProcessPartSummary(part, true))
        .filter(Boolean)
        .join('\n')
    },
    getProcessPartLabel(part) {
      const type = part && part.type ? String(part.type) : 'unknown'
      const tool = part && part.tool ? String(part.tool) : ''
      if (type === 'tool') {
        return tool ? `工具调用: ${tool}` : '工具调用'
      }
      if (type === 'step-start') {
        return '步骤开始'
      }
      if (type === 'step-finish') {
        return '步骤完成'
      }
      return type
    },
    getProcessPartStatus(part) {
      const status = part && part.status ? String(part.status) : ''
      if (!status) return ''
      if (status === 'running') return '进行中'
      if (status === 'completed') return '已完成'
      if (status === 'pending') return '等待中'
      if (status === 'failed') return '失败'
      return status
    },
    getProcessPartSummary(part, withHeader = false) {
      if (!part) return ''
      const header = withHeader
        ? `[${this.getProcessPartLabel(part)}${this.getProcessPartStatus(part) ? ' / ' + this.getProcessPartStatus(part) : ''}]`
        : ''
      if (part.type === 'tool') {
        const state = part.state && typeof part.state === 'object' ? part.state : null
        const lines = []
        if (header) {
          lines.push(header)
        }
        if (state && state.title) {
          lines.push(state.title)
        }
        if (state && state.input && Object.keys(state.input).length) {
          const inputSummary = state.input.description || state.input.title || ''
          lines.push('input: ' + (inputSummary || JSON.stringify(state.input)))
        }
        if (state && state.output) {
          lines.push('output: ' + String(state.output))
        }
        return lines.join('\n')
      }
      const lines = []
      if (header) {
        lines.push(header)
      }
      if (part.text) {
        lines.push(String(part.text))
        return lines.join('\n')
      }
      if (part.state && part.state.raw) {
        lines.push(String(part.state.raw))
        return lines.join('\n')
      }
      return lines.join('\n')
    },
    streamEllipsisOnReasoning(message, messageIndex) {
      if (message.role !== 'assistant' || !this.isCurrentSessionLoading) return false
      if (messageIndex !== this.aiMessages.length - 1) return false
      const hasR = this.messageHtmlToPlain(message.reasoning).length > 0
      const hasC = this.messageHtmlToPlain(message.content).length > 0
      return hasR && !hasC
    },
    streamEllipsisOnText(message, messageIndex) {
      if (message.role !== 'assistant' || !this.isCurrentSessionLoading) return false
      if (messageIndex !== this.aiMessages.length - 1) return false
      return this.messageHtmlToPlain(message.content).length > 0
    },
    /** 展示模型思考过程（与正文分区） */
    displayReasoningHtml(message, messageIndex) {
      const raw = message.reasoning != null ? String(message.reasoning) : ''
      if (this.streamEllipsisOnReasoning(message, messageIndex)) {
        return this.convertHtmlContent(raw) + '<span class="ai-stream-ellipsis" title="思考生成中">......</span>'
      }
      // // console.log("reasoning raw", raw);
      return this.convertHtmlContent(raw)
    },
    /** 流式未结束时在最后一条 AI 正文的末尾加省略号，提示仍在生成 */
    displayMessageContentHtml(message, messageIndex) {
      const raw = message.content != null ? String(message.content) : ''

      if (this.streamEllipsisOnText(message, messageIndex)) {
        return this.convertHtmlContent(raw) + '<span class="ai-stream-ellipsis" title="回答生成中">......</span>'
      }
      return this.convertHtmlContent(raw)
    }
  },
  beforeDestroy() {
    this._teardownHistoryBackForSwipe()
    CordovaUtils.clearCustomBackForOverlay()
    if (this.sseAbortController) {
      this.sseAbortController.abort()
      this.sseAbortController = null
    }
  }
}
</script>


<style scoped lang="scss">
$body-background-color: #f8f8f8;
$content-background-color: #ffffff;
$primary-font-color: #101010;
$minor-font-color: #cecece;

.ai-chat-mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  background: $body-background-color;
}

.ai-chat-header {
  display: grid;
  grid-template-columns: 30px 1fr 30px;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e8ef;
  background: $content-background-color;
  position: relative;
  z-index: 3;
}

.ai-chat-title-wrap {
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 2px;

  p {
    margin: 0;
  }
}

.ai-session-trigger {
  width: 30px;
  height: 30px;
  border: none;
  padding: 0;
  background: transparent;
  color: $minor-font-color;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-session-trigger-icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
}

.ai-chat-close {
  width: 30px;
  height: 30px;
  border: none;
  padding: 0;
  background: transparent;
  color: $minor-font-color;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-chat-close-icon {
  width: 24px;
  height: 24px;
  stroke: currentColor;
  stroke-width: 1.5;
  stroke-linecap: round;
}

.ai-session-drawer-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4;
  background: rgba(0, 0, 0, 0.26);
}

.ai-session-drawer {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 5;
  width: 300px;
  max-width: 75%;
  padding: 16px 14px;
  background: #f3f4f6;
  border-right: 1px solid #e4e7ee;
  transform: translateX(-100%);
  transition: transform 0.2s ease;
  display: flex;
  flex-direction: column;
}

.ai-session-drawer.is-open {
  transform: translateX(0);
}

.ai-session-new-button {
  height: 36px;
  border: 1px solid #cfd4df;
  border-radius: 10px;
  font-size: 14px;
  color: #3b3e45;
  background: #fff;
  margin-bottom: 16px;
}

.ai-session-list {
  overflow-y: auto;
  flex: 1;
}

.ai-session-row {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
  border-radius: 8px;
}

.ai-session-row.is-active {
  background: #e4e6eb;
}

.ai-session-item {
  display: block;
  flex: 1;
  min-width: 0;
  border: none;
  text-align: left;
  background: transparent;
  color: #3b3e45;
  padding: 10px 4px 10px 12px;
  border-radius: 8px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ai-session-delete {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  padding: 0;
  border-radius: 8px;
  background: transparent;
  color: #cecece;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-session-delete:hover {
  color: #e54545;
}

.ai-session-delete:active {
  color: #e54545;
  background: rgba(229, 69, 69, 0.08);
}

.ai-session-delete-icon {
  width: 20px;
  height: 20px;
}

.ai-session-empty {
  color: #8a90a0;
  font-size: 13px;
  text-align: center;
  margin-top: 20px;
}

.ai-chat-title {
  line-height: 20px;
  margin-bottom: 1px;
  font-size: 16px;
  font-weight: 600;
  color: $primary-font-color;
}

.ai-chat-subtitle {
  color: $minor-font-color;
  font-size: 12px;
  line-height: 16px;
}

.ai-chat-body {
  overflow-y: auto;
  flex: 1;
  padding: 12px;
}

.ai-message-item {
  display: flex;
  margin-bottom: 10px;
}

.ai-message-body {
  display: flex;
  flex: 1;
  max-width: calc(100% - 32px);
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.ai-message-body.is-user {
  align-items: flex-end;
}

.ai-reasoning-wrap {
  max-width: 100%;
  width: 100%;
}

.ai-reasoning-label {
  margin: 0 0 6px;
  color: $minor-font-color;
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0;
  word-spacing: 0;
  text-align: left;
}

.ai-reasoning-block {
  max-width: 100%;
  padding: 10px 12px;
  text-align: left;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #4f8cff;
  border-radius: 8px;
  color: $minor-font-color;
  font-size: 12px;
  line-height: 18px;
  word-break: break-word;
  background: #f5f5f5;
  box-sizing: border-box;

  ::v-deep .ai-stream-ellipsis {
    color: $minor-font-color;
  }

  ::v-deep code,
  ::v-deep pre {
    color: $primary-font-color;
  }
}

.ai-process-wrap {
  max-width: 100%;
  width: 100%;
  margin-top: 8px;
}

.ai-process-summary {
  max-width: 100%;
  padding: 10px 12px;
  text-align: left;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #7c9cf5;
  border-radius: 8px;
  background: #f7f8fb;
  font-size: 12px;
  line-height: 18px;
  color: $primary-font-color;
  word-break: break-word;
}

.ai-process-stream {
  max-width: 100%;
  padding: 10px 12px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  border: 1px solid #e8e8e8;
  border-left: 3px solid #7c9cf5;
  border-radius: 8px;
  background: #f7f8fb;
  color: #2f3542;
  box-sizing: border-box;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.7;
}

.ai-message-role {
  width: 24px;
  margin-right: 8px;
  color: $minor-font-color;
  font-size: 12px;
  line-height: 18px;
}

.ai-message-content {
  max-width: calc(100% - 40px);
  padding: 8px 10px;
  border-radius: 10px;
  color: $primary-font-color;
  font-size: 13px;
  line-height: 20px;
  word-break: break-word;
  text-align: left;
  background: $content-background-color;

  /* v-html 内嵌，需穿透 scoped */
  ::v-deep .ai-stream-ellipsis {
    color: $minor-font-color;
  }
}

.ai-message-item.from-ai {
  .ai-message-content {
    max-width: 100%;
    background: #fff;
    border: 1px solid #e5e8ef;
  }
}

.ai-message-item.from-user {
  justify-content: flex-end;

  .ai-message-role {
    order: 2;
    margin-right: 0;
    margin-left: 8px;
    text-align: right;
  }

  .ai-message-content {
    background: #dce8ff;
  }
}

.ai-chat-footer {
  display: flex;
  align-items: flex-end;
  padding: 10px 12px;
  border-top: 1px solid #e5e8ef;
  background: $content-background-color;
}

.ai-input-wrap {
  position: relative;
  width: 100%;
}

.ai-input {
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 55px 8px 10px;
  border: 1px solid #d4d9e5;
  border-radius: 8px;
  resize: none;
  overflow-y: hidden;
  outline: none;
  color: $primary-font-color;
  font-size: 13px;
  background: $body-background-color;
  box-sizing: border-box;
}

.ai-send-button {
  position: absolute;
  right: 12px;
  bottom: 8px;
  border: none;
  border-radius: 4px;
  background: #4f8cff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 12px;
  transition: opacity 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.ai-stop-button {
  position: absolute;
  right: 12px;
  bottom: 8px;
  padding: 6px 15px;
  border: none;
  border-radius: 4px;
  background: #98b6ef;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.ai-stop-icon {
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: currentColor;
}

.ai-send-icon {
  width: 18px;
  height: 18px;
}

/* 加载动画样式 */
.loading-content {
  display: flex;
  align-items: center;
  gap: 8px;
  background: $content-background-color;
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #8a90a0;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }

    &:nth-child(3) {
      animation-delay: 0s;
    }
  }
}

.loading-text {
  font-size: 12px;
  color: $minor-font-color;
}

@keyframes typing {

  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
